/**
 * Ground toolkit shared by the tile sprites: autotile bits, deterministic hashes / PRNG / tileable noise, the
 * grass painter and the rounded path edge that dirt and cobble use to blend into the lawn.
 *
 * 4× density (ADR-0111): a tile is 64×64 art px for 16×16 world px, so every generator draws natively at 64 and
 * keeps its layout in world units (`D` art px each): the same shapes as the reviewed 1× art, with 1-art-px detail.
 *
 * Seamlessness: tone fields are gradient noise with a 64-art-px period whose lattice points ON the tile border are
 * shared by every tile and variant (`tnoise`), so any tile meets any neighbour without a seam; discrete marks
 * (tufts, pebbles) stay inside the tile. Path edges are a signed-distance field whose band depth is a pure
 * function of (side, position along the edge), so straight edges continue across tiles and outer corners (two
 * open sides) round off.
 */
import { type PaletteKey, PixelCanvas } from '../../lib';

/** A procedural frame (the lib's `FrameSource` minus its grid form). */
export type FrameSourceFn = (c: PixelCanvas) => void;

/** Tile size in art px (16 world px at density 4). */
export const T = 64;
/** Art px per world px. */
export const D = 4;

/**
 * The lawn burnt to ash (ADR-0187: the farm in hell): its greens as a recolour, the same in every ground tile's hell twin
 * (`recolor: { hell: ASH }`) so they meet as their lawns do.
 */
export const ASH: Partial<Record<PaletteKey, PaletteKey>> = { g: 'N', l: 'n', G: 'k', D: 'k', y: 'o' };

/** Autotile mask bits: set when that neighbour belongs to the same material. */
export const N = 1;
export const E = 2;
export const S = 4;
export const W = 8;

export const range = (n: number) => Array.from({ length: n }, (_, i) => i);

/** A small hand-drawn grid; `.` = skip. */
export type Stamp = readonly string[];

// ---------------------------------------------------------------------------------------------------------
// Deterministic randomness. Ground tiles key their layout on explicit seeds (per variant / mask) so that
// e.g. a path's grass band is painted exactly like the grass tile it meets.

/** Integer hash in [0,1) for per-pixel decisions on ground tiles. */
export function hash(x: number, y: number, s = 0): number {
  let n = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(s | 0, -2048144777)) >>> 0;
  n = Math.imul(n ^ (n >>> 13), 1274126177) >>> 0;
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
}

/** Avalanche hash in [0,1): well mixed even for small integer inputs (the lib's `hash2` is nearly constant there). */
export function mixHash(a: number, b: number, s = 0): number {
  let h = (Math.imul(a | 0, 0x27d4eb2d) ^ Math.imul(b | 0, 0x165667b1) ^ Math.imul(s | 0, 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Small seeded PRNG (mulberry32). */
export interface Rand {
  next(): number;
  int(lo: number, hi: number): number;
  pick<T>(xs: readonly T[]): T;
}

export function rand(seed: number): Rand {
  let a = (Math.imul(seed | 0, 2654435761) ^ 0x9e3779b9) >>> 0;
  const next = () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return {
    next,
    int: (lo, hi) => lo + Math.floor(next() * (hi - lo + 1)),
    pick: <T>(xs: readonly T[]) => xs[Math.floor(next() * xs.length)] as T,
  };
}

/**
 * Gradient (Perlin) noise with a 64-art-px period (one tile), in [0,1] around 0.5; `scale` = lattice cell in art px
 * (a divisor of 64). Lattice gradients on the tile border (ix or iy ≡ 0) come from `border` and are shared by every
 * tile/variant using the same `border`; interior ones come from `seed`, so variants differ inside but always meet
 * seamlessly. `spread` scales the border gradients (smaller = calmer seams). Sampled at pixel centres; value is 0.5
 * exactly on lattice points. The lattice is the 1× art's (same indices, same gradients), sampled 4× finer.
 */
export function tnoise(x: number, y: number, scale: number, seed: number, border = 0, spread = 1): number {
  const p = T / scale;
  const g = (ix: number, iy: number): [number, number] => {
    const a = ((ix % p) + p) % p;
    const b = ((iy % p) + p) % p;
    const edge = a === 0 || b === 0;
    const ang = hash(a, b, edge ? border + 9001 : seed) * Math.PI * 2;
    const m = edge ? spread : 1;
    return [Math.cos(ang) * m, Math.sin(ang) * m];
  };
  const fx = (x + 0.5) / scale;
  const fy = (y + 0.5) / scale;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = fx - x0;
  const ty = fy - y0;
  const dot = (ix: number, iy: number, dx: number, dy: number) => {
    const [gx, gy] = g(ix, iy);
    return gx * dx + gy * dy;
  };
  const f = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);
  const sx = f(tx);
  const sy = f(ty);
  const n00 = dot(x0, y0, tx, ty);
  const n10 = dot(x0 + 1, y0, tx - 1, ty);
  const n01 = dot(x0, y0 + 1, tx, ty - 1);
  const n11 = dot(x0 + 1, y0 + 1, tx - 1, ty - 1);
  const top = n00 + (n10 - n00) * sx;
  const bot = n01 + (n11 - n01) * sx;
  return 0.5 + (top + (bot - top) * sy) * 0.9;
}

// ---------------------------------------------------------------------------------------------------------
// Stamps

/** Stamps `grid` at x,y only if every inked pixel passes `ok`. Returns false if it did not fit. */
export function stampIf(c: PixelCanvas, grid: Stamp, x: number, y: number, ok: (x: number, y: number) => boolean) {
  for (let j = 0; j < grid.length; j++) {
    const row = grid[j] as string;
    for (let i = 0; i < row.length; i++) if (row[i] !== '.' && !ok(x + i, y + j)) return false;
  }
  for (let j = 0; j < grid.length; j++) {
    const row = grid[j] as string;
    for (let i = 0; i < row.length; i++) if (row[i] !== '.') c.set(x + i, y + j, row[i] as PaletteKey);
  }
  return true;
}

/** Pixels of a drawn mark: x, y, colour. */
export type Mark = readonly (readonly [number, number, PaletteKey])[];

/** Draws `mark` moved by (x, y) only if every pixel passes `ok`. Returns false if it did not fit. */
export function markIf(c: PixelCanvas, mark: Mark, x: number, y: number, ok: (x: number, y: number) => boolean) {
  if (!mark.every(([i, j]) => ok(x + i, y + j))) return false;
  for (const [i, j, k] of mark) c.set(x + i, y + j, k);
  return true;
}

/** A stamp as a mark (its inked pixels). */
export const markOf = (s: Stamp): Mark =>
  s.flatMap((row, j) => [...row].flatMap((ch, i) => (ch === '.' ? [] : [[i, j, ch as PaletteKey] as const])));

const bounds = (m: Mark) => {
  let [x0, y0, x1, y1] = [99, 99, -99, -99];
  for (const [x, y] of m) [x0, y0, x1, y1] = [Math.min(x0, x), Math.min(y0, y), Math.max(x1, x), Math.max(y1, y)];
  return { x0, y0, x1, y1 };
};

/**
 * Scatters marks with Poisson-ish spacing inside a `size`-px canvas (`margin` px clear of its edges, so a mark never
 * crosses a tile seam); `minDist` between mark centres.
 */
export function scatterMarks(
  c: PixelCanvas,
  r: Rand,
  count: number,
  shapes: readonly Mark[],
  ok: (x: number, y: number) => boolean,
  minDist: number,
  margin = 2,
  placed: [number, number][] = [],
  size = T,
): [number, number][] {
  const start = placed.length;
  for (let tries = 0; placed.length - start < count && tries < count * 40; tries++) {
    const m = r.pick(shapes);
    const b = bounds(m);
    const x = r.int(margin - b.x0, size - 1 - margin - b.x1);
    const y = r.int(margin - b.y0, size - 1 - margin - b.y1);
    const cx = x + (b.x0 + b.x1) / 2;
    const cy = y + (b.y0 + b.y1) / 2;
    if (placed.some(([px, py]) => Math.hypot(px - cx, py - cy) < minDist)) continue;
    if (markIf(c, m, x, y, ok)) placed.push([cx, cy]);
  }
  return placed;
}

// ---------------------------------------------------------------------------------------------------------
// Grass. Tiles stay calm: flat base green plus a few small tufts of 1-art-px blades; macro variation comes from
// `decal_grass`.

/**
 * A tuft of `n` thin blades fanning out of one root (bottom-centre at 0,0), the 1× tufts' colours: shadowed `G` feet,
 * lit `l` tips; the blades leaning towards the light (left) are lit further down, the ones leaning away mostly shaded,
 * so the clump has a lit and a shaded side. The middle blades are tallest, the outer ones lean out and curl.
 */
export function tuft(
  n: number,
  h: number,
  seed: number,
  tips: PaletteKey = 'l',
  body: PaletteKey = 'G',
  spread = 1,
): Mark {
  const r = rand(seed);
  const out = new Map<string, [number, number, PaletteKey]>();
  const put = (x: number, y: number, k: PaletteKey) => {
    const key = `${x},${y}`;
    // Tips win over bodies (a lit tip stays lit where a blade crosses another).
    if (out.get(key)?.[2] === tips && k !== tips) return;
    out.set(key, [x, y, k]);
  };
  const mid = (n - 1) / 2;
  for (let i = 0; i < n; i++) {
    const side = n === 1 ? (r.next() - 0.5) * 2 : (i - mid) / Math.max(1, mid);
    const bh = Math.max(2, Math.round(h * (1 - Math.abs(side) * 0.3) + (r.next() - 0.5) * 1.4));
    const lean = (side * (0.35 + r.next() * 0.3) + (r.next() - 0.5) * 0.2) * spread;
    // Roots huddle within a px or two of the centre.
    const x0 = Math.round((i - mid) * 0.6 * Math.min(spread, 1.5));
    const lit = Math.max(1, Math.round(bh * (side < -0.2 ? 0.6 : side > 0.2 ? 0.3 : 0.45)));
    for (let t = 0; t < bh; t++) {
      const u = t / Math.max(1, bh - 1);
      const x = x0 + Math.round(lean * bh * u * u * 0.8);
      put(x, -t, t >= bh - lit ? tips : body);
    }
  }
  return [...out.values()];
}

/** Light direction in the ground plane (top-left), unit. */
export const L2 = (() => {
  const l = Math.hypot(0.55, 0.75);
  return [-0.55 / l, -0.75 / l] as const;
})();

/** Leaf half-width at t ∈ [0,1] along it: ovate, widest at 35 %, pointed tip. */
export const leafProfile = (t: number) => (t < 0.35 ? Math.sqrt(t / 0.35) : ((1 - t) / 0.65) ** 0.85);

export interface LeafLook {
  body: PaletteKey;
  /** Edge on the half facing the light. */
  lit: PaletteKey;
  /** Edge on the half turned away. */
  shade: PaletteKey;
  /** Midrib (wide leaves only). */
  rib?: PaletteKey;
}

/**
 * A pointed leaf lying flat from its stem end (x, y) along `ang`: `len` long, `wid` half-width at its widest; a 1-px
 * lit edge on the half facing the light, a shaded edge on the other, an optional midrib. Pixels failing `ok` are skipped.
 */
export function leaf(
  c: PixelCanvas,
  x: number,
  y: number,
  ang: number,
  len: number,
  wid: number,
  k: LeafLook,
  ok: (x: number, y: number) => boolean = () => true,
): void {
  const dx = Math.cos(ang);
  const dy = Math.sin(ang);
  const perp = -dy * L2[0] + dx * L2[1];
  const r = Math.ceil(len + wid);
  for (let py = Math.floor(y - r); py <= y + r; py++) {
    for (let px = Math.floor(x - r); px <= x + r; px++) {
      const rx = px + 0.5 - x;
      const ry = py + 0.5 - y;
      const u = rx * dx + ry * dy;
      if (u < 0 || u > len) continue;
      const t = u / len;
      const w = wid * leafProfile(t);
      const v = -rx * dy + ry * dx;
      if (w < 0.35 || Math.abs(v) > w || !ok(px, py)) continue;
      const s = v / w;
      const edge = Math.abs(v) > w - 1;
      let col = k.body;
      if (edge) col = s * perp > 0 ? k.lit : k.shade;
      else if (k.rib && wid > 2.5 && Math.abs(v) < 0.5 && t > 0.1 && t < 0.8) col = k.rib;
      c.set(px, py, col);
    }
  }
}

/** A 1-px contact shadow of colour `k` under (and down-right of) every opaque pixel of `shape`, where `ok`. */
export function contactShadow(
  c: PixelCanvas,
  shape: PixelCanvas,
  k: PaletteKey,
  ok: (x: number, y: number) => boolean,
) {
  for (let y = 0; y < c.h; y++)
    for (let x = 0; x < c.w; x++)
      if (shape.get(x, y) === null && (shape.get(x, y - 1) !== null || shape.get(x - 1, y - 1) !== null) && ok(x, y))
        c.set(x, y, k);
  c.blit(shape);
}

/** The lawn's tuft vocabulary: two-blade sprigs, three-blade tufts, a few fuller fans. */
const SPRIGS: readonly Mark[] = range(6).map((i) => tuft(2, 5 + (i % 2), 40 + i));
const TUFTS: readonly Mark[] = range(6).map((i) => tuft(3 + (i % 2), 6 + (i % 3 === 0 ? 1 : 0), 60 + i));
/** One bigger tuft (full grass tiles only; a path's grass band stays plainer). */
export const FANS: readonly Mark[] = range(4).map((i) => tuft(5 + (i % 2), 8 + (i % 2), 80 + i));

/**
 * Flat `g` where `inside` holds (whole tile, or a path's grass band) plus a few small tufts, keyed by `seed` (none when
 * `seed` is negative: an inner corner's wedge of lawn is too small for one).
 */
export function paintGrass(c: PixelCanvas, seed: number, inside: (x: number, y: number) => boolean = () => true): void {
  for (let y = 0; y < T; y++) for (let x = 0; x < T; x++) if (inside(x, y)) c.set(x, y, 'g');
  if (seed < 0) return;
  const r = rand(seed * 7 + 3);
  const ok = (x: number, y: number) => x >= 0 && y >= 0 && x < T && y < T && inside(x, y);
  const placed = scatterMarks(c, r, r.int(2, 3), TUFTS, ok, 22);
  scatterMarks(c, r, r.int(1, 2), SPRIGS, ok, 16, 2, placed);
}

// ---------------------------------------------------------------------------------------------------------
// Path edges (dirt + cobble share them so lanes and plazas feel like one ground).

/** In world px (the 1× art's numbers): the field is evaluated at art-px centres, so the edge is the same curve. */
const EDGE = {
  /** Grass band depth at the tile corners (shared by all masks → edges continue across tiles). */
  d0: 1.7,
  /** Extra depth mid-edge (smooth bump, 0 at the corners). */
  amp: 1.2,
  /** Second harmonic (asymmetry), px. */
  h2: 0.6,
  /** Outer-corner radius. */
  R: 5,
  /** Noise wobble amplitude on the boundary, px. */
  wob: 1,
} as const;

const PHASE = [0.7, 2.1, 3.9, 5.3];

/** An inner corner's two sides (0 N, 1 E, 2 S, 3 W), by corner: 0 NE, 1 SE, 2 SW, 3 NW (the client's `inner` order). */
export const CORNER_SIDES = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 0],
] as const;

/** Radius (world px) that rounds a path's concave corner where an inner-corner wedge of lawn closes it. */
const INNER_R = 1.2;

/** Grass band depth (world px) of side `side` at position `t` along it: shared by every mask, so edges continue. */
const edgeDepth = (side: number, t: number) => {
  const u = t / 16;
  const bump = 0.5 - 0.5 * Math.cos(2 * Math.PI * u);
  const h = Math.sin(4 * Math.PI * u + (PHASE[side] as number)) - Math.sin(PHASE[side] as number);
  return EDGE.d0 + EDGE.amp * bump + EDGE.h2 * h * 0.5;
};

/** Distance (world px) into the path from side `side`'s grass band, at world point (px, py) of the tile. */
const sideDist = (side: number, px: number, py: number) =>
  [py - edgeDepth(0, px), 16 - px - edgeDepth(1, py), 16 - py - edgeDepth(2, px), px - edgeDepth(3, py)][
    side
  ] as number;

/** Is art pixel (x, y) in the outer world px of side `side` (always lawn when that side is open)? */
const onRim = (side: number, x: number, y: number) => [y < D, x >= T - D, y >= T - D, x < D][side] as boolean;

/**
 * Signed distance into the path in world px (negative = grass) at art pixel x,y, for a mask of PATH neighbours. With
 * `corner`, the inner corner's field instead (a path cell whose two sides of that corner are path while the diagonal
 * isn't): lawn only where both of the corner's side bands would be, the strips of the two neighbours meeting in a
 * square corner of lawn, its point rounded by `INNER_R` — the edges continue the neighbours' bands across the seams.
 */
function pathField(mask: number, corner?: number): (x: number, y: number) => number {
  if (corner !== undefined) {
    const [s1, s2] = CORNER_SIDES[corner] as readonly [number, number];
    return (x, y) => {
      const px = (x + 0.5) / D;
      const py = (y + 0.5) / D;
      const w = (tnoise(x, y, 4 * D, 10023 + corner, 6097, 1) - 0.5) * 2 * EDGE.wob;
      const side = (s: number) => {
        const a = sideDist(s, px, py) + w;
        return onRim(s, x, y) ? Math.min(a, -0.3) : a;
      };
      // A rounded quarter-plane of lawn (p, q: depth into it from each side's edge): its Euclidean signed distance, so
      // the path's bands round the corner instead of squaring it.
      const u = INNER_R + side(s1);
      const v = INNER_R + side(s2);
      return Math.hypot(Math.max(u, 0), Math.max(v, 0)) + Math.min(Math.max(u, v), 0) - INNER_R;
    };
  }
  const open = [!(mask & N), !(mask & E), !(mask & S), !(mask & W)];
  return (x, y) => {
    const px = (x + 0.5) / D;
    const py = (y + 0.5) / D;
    const w = (tnoise(x, y, 4 * D, 10007 + mask, 6097, 1) - 0.5) * 2 * EDGE.wob;
    const a = [0, 1, 2, 3].map((s) => (open[s] ? sideDist(s, px, py) : 99));
    let f = Math.min(...a);
    for (const [i, j] of [
      [0, 3],
      [0, 1],
      [2, 3],
      [2, 1],
    ] as const) {
      const p = a[i] as number;
      const q = a[j] as number;
      if (p < EDGE.R && q < EDGE.R) f = Math.min(f, EDGE.R - Math.hypot(EDGE.R - p, EDGE.R - q));
    }
    // An open side always keeps its outer world px of grass (so the neighbour cell's grass meets grass).
    const rim = [0, 1, 2, 3].some((s) => open[s] && onRim(s, x, y));
    return rim ? Math.min(f + w, -0.3) : f + w;
  };
}

export interface Edge {
  mask: number;
  /** Distance into the path in world px (negative = grass). */
  f: (x: number, y: number) => number;
  grass: (x: number, y: number) => boolean;
  /** Outward normal (from grass into path) · light direction, <0 = shadow side. Only meaningful near the edge. */
  litDot: (x: number, y: number) => number;
  /** Unit outward normal (grass → path). */
  normal: (x: number, y: number) => [number, number];
}

/** The edge of a path tile with neighbour `mask`, or of inner corner `corner`'s wedge (then `mask` is 16 + corner). */
export function makeEdge(mask: number, corner?: number): Edge {
  const f = pathField(mask, corner);
  if (corner !== undefined) mask = 16 + corner;
  const M = 3;
  const G = T + 2 * M;
  const grid = new Float32Array(G * G);
  for (let y = -M; y < T + M; y++) for (let x = -M; x < T + M; x++) grid[(y + M) * G + x + M] = f(x, y);
  const F = (x: number, y: number) =>
    grid[(Math.max(-M, Math.min(T + M - 1, y)) + M) * G + Math.max(-M, Math.min(T + M - 1, x)) + M] as number;
  const normal = (x: number, y: number): [number, number] => {
    const gx = F(x + 2, y) - F(x - 2, y);
    const gy = F(x, y + 2) - F(x, y - 2);
    const l = Math.hypot(gx, gy) || 1;
    return [gx / l, gy / l];
  };
  const litDot = (x: number, y: number) => {
    const [nx, ny] = normal(x, y);
    return nx * -0.55 + ny * -0.83;
  };
  return { mask, f: F, grass: (x, y) => F(x, y) < 0, litDot, normal };
}

/**
 * An inner corner's wedge: `art` (the full tile with the corner redrawn) wherever it differs from `full` (the tile the
 * client draws under it), transparent elsewhere, onto `c`.
 */
export function wedge(c: PixelCanvas, full: PixelCanvas, art: PixelCanvas): void {
  for (let y = 0; y < c.h; y++)
    for (let x = 0; x < c.w; x++) if (art.get(x, y) !== full.get(x, y)) c.set(x, y, art.get(x, y));
}

/** A path inner corner (`CORNER_SIDES` order): the lawn closing it, with the lips, bands and blades of its edges. */
export function pathInner(material: (c: PixelCanvas) => void, look: EdgeLook, corner: number): FrameSourceFn {
  return (c) => {
    const full = new PixelCanvas(T, T);
    material(full);
    const art = full.clone();
    blendEdge(art, makeEdge(15, corner), look, -1);
    wedge(c, full, art);
  };
}

/** How the path material darkens where it meets the grass (art px bands, from the grass outward). */
export interface EdgeLook {
  /** Shadow side (the N/W banks: the grass's lip and its 3/4 earth face): colours, `null` = one step darker. */
  shade: readonly (PaletteKey | null)[];
  /** Lit side (S/E: the grass stands in front of the path in the light): a thin contact line. */
  lit: readonly (PaletteKey | null)[];
}

const darkerOf: Partial<Record<PaletteKey, PaletteKey>> = {
  m: 'n',
  n: 'N',
  t: 'm',
  '4': '3',
  '3': '2',
  '2': '1',
  '1': 'k',
};

/** 0 → 1 smoothly over [a, b]. */
const ramp = (v: number, a: number, b: number) => Math.max(0, Math.min(1, (v - a) / (b - a)));

/**
 * Lays grass over the open sides of a path tile that already holds its material, then the grass's lip (a lit `l` line
 * on the S/E banks, a shaded `G` lip on the N/W banks), the contact bands on the material, and blades of the lawn
 * overhanging the path (standing up over the lit banks, drooping over the shaded ones).
 */
export function blendEdge(c: PixelCanvas, e: Edge, look: EdgeLook, grassSeed: number): void {
  if (e.mask === 15) return;
  const mat = c.clone();
  // Tufts stay a little away from the edge (the lip and the overhang own it).
  paintGrass(c, grassSeed, (x, y) => e.f(x, y) < -0.5);
  for (let y = 0; y < T; y++) {
    for (let x = 0; x < T; x++) {
      const v = e.f(x, y);
      if (v < -0.5) continue;
      const a = v * D;
      const ld = e.litDot(x, y);
      if (v < 0) {
        c.set(x, y, 'g');
        // The lip: lit l on the banks facing the light, G under the shaded ones.
        if (a > -1 && ld > 0.3) c.set(x, y, 'l');
        else if (a > -1 && ld < -0.25) c.set(x, y, 'G');
        else if (a > -2 && ld < -0.65) c.set(x, y, 'G');
        continue;
      }
      const p = mat.get(x, y) as PaletteKey;
      const bands = ld < 0 ? look.shade : look.lit;
      const n = Math.round(bands.length * (ld < 0 ? ramp(-ld, 0.05, 0.6) : ramp(ld, 0.05, 0.4)));
      const i = Math.floor(a);
      if (i < n) c.set(x, y, bands[i] ?? darkerOf[p] ?? p);
    }
  }
  overhang(c, e);
}

/**
 * Blades of the lawn overhanging the path: rooted on the grass's lip, leaning out along the edge's normal and up
 * (grass stands): long lit blades over the S/E banks, short shaded tips drooping over the N/W banks. Whole blades only
 * (a blade that would cross the tile seam is skipped), roots at least 3 px apart.
 */
function overhang(c: PixelCanvas, e: Edge): void {
  const roots: [number, number][] = [];
  for (let y = 2; y < T - 2; y++) {
    for (let x = 2; x < T - 2; x++) {
      const v = e.f(x, y);
      if (v >= 0 || v < -0.3) continue;
      if (hash(x, y, 4711 + e.mask) > 0.3) continue;
      if (roots.some(([rx, ry]) => Math.hypot(rx - x, ry - y) < 5)) continue;
      const [nx, ny] = e.normal(x, y);
      const ld = nx * -0.55 + ny * -0.83;
      const lit = ld > 0.15;
      const droop = ny > 0.5;
      // Blades stand: up-biased out of the lip; over a north bank's earth face they droop straight down.
      let [dx, dy] = droop ? [nx * 0.3, 1] : [nx, ny - 0.9];
      const l = Math.hypot(dx, dy) || 1;
      dx /= l;
      dy /= l;
      const h = hash(x, y, 4712);
      const nb = 1 + Math.floor(h * 3);
      const blades: [number, number][][] = [];
      for (let k = 0; k < nb; k++) {
        const a = (k - (nb - 1) / 2) * 0.5 + (hash(x, y, 4713 + k) - 0.5) * 0.3;
        const [bx, by] = [dx * Math.cos(a) - dy * Math.sin(a), dx * Math.sin(a) + dy * Math.cos(a)];
        const len = droop
          ? 2 + Math.floor(hash(x, y, 4720 + k) * 2)
          : (lit ? 3 : 2) + Math.floor(hash(x, y, 4720 + k) * 3);
        const px: [number, number][] = [];
        for (let t = 1; t <= len; t++) px.push([Math.round(x + bx * t), Math.round(y + by * t)]);
        blades.push(px);
      }
      const all = blades.flat();
      if (all.some(([bx, by]) => bx < 1 || by < 1 || bx > T - 2 || by > T - 2)) continue;
      // It must reach over the path.
      if (!all.some(([bx, by]) => e.f(bx, by) >= 0)) continue;
      roots.push([x, y]);
      for (const px of blades)
        px.forEach(([bx, by], k) => {
          if (e.f(bx, by) < -0.3) return;
          const tip = k >= px.length - (px.length > 3 ? 2 : 1);
          c.set(bx, by, lit ? (tip ? 'l' : 'g') : tip ? 'g' : 'G');
        });
    }
  }
}
